import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  RefreshCw,
  Play,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Settings,
  FileText,
  BarChart3,
  RotateCcw,
  Loader2,
  Lock,
  Plus,
  Eye,
  Send,
  Trash2,
  X,
  Pencil,
  Check,
  Download,
  ShieldCheck,
  Info,
} from 'lucide-react'

import { apiClient, ApiError } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'

interface BatchMaterialsInfo {
  generated_at: string | null
  generated_by_name: string | null
  accounting_count: number
  inspection_count: number
  invoice_count: number
  status: string // "entries_ready" | "invoiced" | "sent" | "published"
  any_sent: boolean
}

interface BillExportGroup {
  bill_export_uuid: string | null
  max_date: string | null
  total: number
  not_fetched: number
  fetched: number
  update_available: number
  has_materials: boolean
  materials: BatchMaterialsInfo | null
}

interface ActiveOperationInfo {
  id: string
  operation_type: string
  bill_export_uuid: string | null
  started_by_name: string | null
  started_at: string | null
}

interface BillsSummaryResponse {
  groups: BillExportGroup[]
  totals: BillExportGroup
  active_operation: ActiveOperationInfo | null
}

interface FetchIndexResponse {
  bills_total: number
}

interface FetchSalesDataResponse {
  queued: number
}

interface GenerateEntriesResponse {
  bills_processed: number
  bills_skipped: number
  accounting_entries: number
  inspection_entries: number
  auto_blocked: number
}

interface GeneratePreviewResponse {
  total_bills: number
  free_bills: number
  invoiced_bills: number
}

interface InvoicePeriod {
  id: string
  sub_organization_uuid: string
  organization_name: string | null
  start_date: string
  end_date: string
  invoice_date: string
  due_date: string | null
  status: string
  created_at: string
  created_by: string | null
  closed_at: string | null
  invoice_count: number
  total_amount: number | null
}

interface InvoicePeriodListResponse {
  data: InvoicePeriod[]
}

interface OrgOption {
  id: string
  name: string
  ahola_sub_org_uuid: string | null
  settings?: {
    fina?: {
      enabled?: boolean
    }
  }
}

interface DeductiblePeriod {
  id: string
  organization_id: string
  organization_name: string | null
  year_month: string
  status: string
  generated_at: string | null
  approved_at: string | null
  line_count: number
  total_copayment: number | string
  progress_total: number | null
  progress_current: number | null
  progress_phase: string | null
  error_message: string | null
  invoice_number_first: number | null
  invoice_number_last: number | null
  planned_invoice_count: number | null
  planned_credit_note_count: number | null
  created_at: string | null
}

interface DeductiblePeriodSummary {
  period_id: string
  organization_name: string | null
  year_month: string
  status: string
  las_line_count: number
  lar_line_count: number
  zeroed_line_count: number
  invoice_count_total: number
  charge_invoice_count: number
  credit_invoice_count: number
  total_amount: number | string
  charge_amount: number | string
  credit_amount: number | string
  credit_note_line_count: number
  credit_line_count: number
  mixed_credit_line_count: number
  original_line_count: number
  correction_line_count: number
}

function formatEur(val: number | string): string {
  return (
    Number(val).toLocaleString('fi-FI', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + ' €'
  )
}

function formatUuid(uuid: string | null): string {
  if (!uuid) return ''
  return uuid.length > 12 ? `${uuid.slice(0, 8)}..${uuid.slice(-4)}` : uuid
}

function formatDateTime(isoStr: string | null): string {
  if (!isoStr) return ''
  const d = new Date(isoStr)
  return d.toLocaleDateString('fi-FI') + ' ' + d.toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' })
}

export default function PaymentTrafficPage() {
  const { t } = useTranslation('billing')
  const { t: tc } = useTranslation('common')
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Date range only for fetch-index (not for summary visibility)
  const today = new Date().toISOString().slice(0, 10)
  const firstOfMonth = today.slice(0, 8) + '01'
  const [dateFrom, setDateFrom] = useState(firstOfMonth)
  const [dateTo, setDateTo] = useState(today)
  const [banner, setBanner] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [expandedRow, setExpandedRow] = useState<string | null>(null)
  const [processingRowKey, setProcessingRowKey] = useState<string | null>(null)
  const [processingPhase, setProcessingPhase] = useState<string | null>(null)

  // -- Regeneration confirmation dialog state --
  const [regenConfirm, setRegenConfirm] = useState<{
    exportUuid: string | null
    preview: GeneratePreviewResponse
  } | null>(null)

  // -- Invoice periods state --
  const [showCreatePeriod, setShowCreatePeriod] = useState(false)
  const [newPeriodOrg, setNewPeriodOrg] = useState('')
  const [newPeriodStart, setNewPeriodStart] = useState('')
  const [newPeriodEnd, setNewPeriodEnd] = useState('')
  const [newPeriodInvoiceDate, setNewPeriodInvoiceDate] = useState('')
  const [newPeriodDueDate, setNewPeriodDueDate] = useState('')
  const [editingPeriod, setEditingPeriod] = useState<{ id: string; invoice_date: string; due_date: string } | null>(null)

  const showBanner = (type: 'success' | 'error', message: string) => {
    setBanner({ type, message })
    setTimeout(() => setBanner(null), type === 'success' ? 5000 : 10000)
  }

  const batchBody = (exportUuid: string | null) => {
    const body: { bill_export_uuid: string | null; date_from?: string; date_to?: string } = {
      bill_export_uuid: exportUuid,
    }
    // Only include date filter for open bills (no batch UUID)
    if (!exportUuid) {
      body.date_from = dateFrom
      body.date_to = dateTo
    }
    return body
  }

  const clearProcessing = () => {
    setProcessingRowKey(null)
    setProcessingPhase(null)
  }

  const handleMutationError = (err: Error) => {
    clearProcessing()
    if (err instanceof ApiError && err.code === 'OPERATION_LOCKED') {
      const details = err.details as { operation_type?: string; started_by?: string } | undefined
      const opLabel = details?.operation_type
        ? t(`operations.${details.operation_type}`, { defaultValue: details.operation_type })
        : ''
      showBanner('error', t('operations.locked', { operation: opLabel, user: details?.started_by || '' }))
      queryClient.invalidateQueries({ queryKey: ['billsSummary'] })
    } else {
      showBanner('error', `${t('progress.error')}: ${err.message}`)
    }
  }

  // Bills summary query with polling — always loads all batches
  const { data, isLoading, error } = useQuery<BillsSummaryResponse>({
    queryKey: ['billsSummary'],
    queryFn: () => apiClient.get('/billing/bills/summary'),
    refetchInterval: (query) => {
      const d = query.state.data
      // Fast polling during active operations
      if (d?.active_operation) {
        return 5000
      }
      const totals = d?.totals
      if (totals && (totals.not_fetched > 0 || totals.update_available > 0)) {
        return 20000
      }
      return false
    },
  })

  // Fetch index mutation
  const fetchIndexMutation = useMutation({
    mutationFn: (body: { period_start: string; period_end: string }) =>
      apiClient.post<FetchIndexResponse>('/billing/fetch-index', body),
    onSuccess: (result: FetchIndexResponse) => {
      queryClient.invalidateQueries({ queryKey: ['billsSummary'] })
      showBanner('success', t('progress.indexComplete', { total: result.bills_total }))
    },
    onError: handleMutationError,
  })

  // Fetch sales data mutation
  const fetchSalesDataMutation = useMutation({
    mutationFn: (body: {
      bill_export_uuid: string | null
      date_from?: string
      date_to?: string
    }) => apiClient.post<FetchSalesDataResponse>('/billing/fetch-sales-data', body),
    onSuccess: (result: FetchSalesDataResponse) => {
      if (result.queued > 0) {
        showBanner('success', t('bills.fetchQueued', { count: result.queued }))
      }
    },
    onError: handleMutationError,
  })

  // Preview regeneration (for confirmation dialog)
  const previewMutation = useMutation({
    mutationFn: (body: {
      bill_export_uuid: string | null
      date_from?: string
      date_to?: string
    }) => apiClient.post<GeneratePreviewResponse>('/billing/generate-materials/preview', body),
  })

  // Phase 1: Generate entries
  const generateEntriesMutation = useMutation({
    mutationFn: (body: {
      bill_export_uuid: string | null
      date_from?: string
      date_to?: string
    }) => apiClient.post<GenerateEntriesResponse>('/billing/generate-materials', body),
    onSuccess: (result: GenerateEntriesResponse) => {
      clearProcessing()
      queryClient.invalidateQueries({ queryKey: ['billsSummary'] })
      const skippedInfo = result.bills_skipped > 0
        ? ` (${t('bills.billsSkipped', { count: result.bills_skipped })})`
        : ''
      showBanner(
        'success',
        t('bills.entriesGenerated', {
          accounting: result.accounting_entries,
          inspection: result.inspection_entries,
        }) + skippedInfo
      )
    },
    onError: handleMutationError,
  })

  // -- Invoice periods queries & mutations --
  const { data: periodsData, isLoading: periodsLoading } = useQuery<InvoicePeriodListResponse>({
    queryKey: ['invoicePeriods'],
    queryFn: () => apiClient.get('/billing/invoice-periods'),
  })

  const { data: orgsResp } = useQuery<{ data: OrgOption[] }>({
    queryKey: ['organizations'],
    queryFn: () => apiClient.get('/organizations'),
    staleTime: 5 * 60 * 1000,
  })
  const orgsWithAhola = (orgsResp?.data || []).filter((o) => o.ahola_sub_org_uuid)

  const createPeriodMutation = useMutation({
    mutationFn: (body: { sub_organization_uuid: string; start_date: string; end_date: string; invoice_date: string; due_date?: string }) =>
      apiClient.post('/billing/invoice-periods', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoicePeriods'] })
      setShowCreatePeriod(false)
      showBanner('success', t('periods.createSuccess'))
    },
    onError: (err: Error) => showBanner('error', err.message),
  })

  const createPeriodInvoicesMutation = useMutation({
    mutationFn: (periodId: string) =>
      apiClient.post<{ invoices_created: number }>(`/billing/invoice-periods/${periodId}/create-invoices`),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['invoicePeriods'] })
      showBanner('success', t('periods.invoicesCreated', { count: result.invoices_created }))
    },
    onError: handleMutationError,
  })

  const deletePeriodInvoicesMutation = useMutation({
    mutationFn: (periodId: string) =>
      apiClient.post<{ deleted: number }>(`/billing/invoice-periods/${periodId}/delete-invoices`),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['invoicePeriods'] })
      showBanner('success', t('periods.invoicesDeleted', { count: result.deleted }))
    },
    onError: (err: Error) => showBanner('error', err.message),
  })

  const deletePeriodMutation = useMutation({
    mutationFn: (periodId: string) =>
      apiClient.delete(`/billing/invoice-periods/${periodId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoicePeriods'] })
      showBanner('success', t('periods.periodDeleted'))
    },
    onError: (err: Error) => showBanner('error', err.message),
  })

  const updatePeriodMutation = useMutation({
    mutationFn: ({ periodId, body }: { periodId: string; body: { invoice_date?: string; due_date?: string } }) =>
      apiClient.patch(`/billing/invoice-periods/${periodId}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoicePeriods'] })
      setEditingPeriod(null)
    },
    onError: (err: Error) => showBanner('error', err.message),
  })

  // -- Deductible periods state --
  const [showCreateDeductible, setShowCreateDeductible] = useState(false)
  const [newDeductibleOrg, setNewDeductibleOrg] = useState('')
  const [newDeductibleMonth, setNewDeductibleMonth] = useState('')

  const orgsWithFina = (orgsResp?.data || []).filter((o) => o.settings?.fina?.enabled)

  const { data: deductiblePeriodsData, isLoading: deductiblePeriodsLoading } = useQuery<DeductiblePeriod[]>({
    queryKey: ['deductiblePeriods'],
    queryFn: () => apiClient.get('/billing/deductibles/periods'),
    refetchInterval: (query) => {
      const periods = query.state.data
      if (periods?.some((p) => p.status === 'processing' || p.status === 'cancelling')) return 5000
      return false
    },
  })

  const createDeductibleMutation = useMutation({
    mutationFn: (body: { organization_id: string; year_month: string }) =>
      apiClient.post('/billing/deductibles/periods', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deductiblePeriods'] })
      setShowCreateDeductible(false)
      showBanner('success', t('deductibles.createSuccess'))
    },
    onError: (err: Error) => showBanner('error', err.message),
  })

  const generateDeductibleMutation = useMutation({
    mutationFn: (periodId: string) =>
      apiClient.post(`/billing/deductibles/periods/${periodId}/generate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deductiblePeriods'] })
      showBanner('success', t('deductibles.generateStarted'))
    },
    onError: (err: Error) => showBanner('error', err.message),
  })

  const approveDeductibleMutation = useMutation({
    mutationFn: (periodId: string) =>
      apiClient.post(`/billing/deductibles/periods/${periodId}/approve`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deductiblePeriods'] })
      showBanner('success', t('deductibles.approveSuccess'))
    },
    onError: (err: Error) => showBanner('error', err.message),
  })

  const deleteDeductibleMutation = useMutation({
    mutationFn: (periodId: string) =>
      apiClient.delete(`/billing/deductibles/periods/${periodId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deductiblePeriods'] })
      showBanner('success', t('deductibles.periodDeleted'))
    },
    onError: (err: Error) => showBanner('error', err.message),
  })

  const cancelDeductibleMutation = useMutation({
    mutationFn: (periodId: string) =>
      apiClient.post(`/billing/deductibles/periods/${periodId}/cancel`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deductiblePeriods'] })
      showBanner('success', t('deductibles.cancelSuccess'))
      // Keep polling — cancelling status will resolve to open
    },
    onError: (err: Error) => showBanner('error', err.message),
  })

  const handleDownloadDat = async (periodId: string, fileType: 'las' | 'lar') => {
    try {
      const token = await apiClient.getToken()
      const baseUrl = apiClient.baseUrl
      const resp = await fetch(
        `${baseUrl}/billing/deductibles/periods/${periodId}/download?file_type=${fileType}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!resp.ok) throw new Error('Download failed')
      const blob = await resp.blob()
      const disposition = resp.headers.get('Content-Disposition') || ''
      const match = disposition.match(/filename="?([^"]+)"?/)
      const filename = match?.[1] || `deductible_${fileType}.dat`
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      showBanner('error', (err as Error).message)
    }
  }

  const [excelLoading, setExcelLoading] = useState<string | null>(null)

  // Deductible material summary modal (reconciliation figures)
  const [summaryPeriodId, setSummaryPeriodId] = useState<string | null>(null)
  const [summary, setSummary] = useState<DeductiblePeriodSummary | null>(null)
  const [summaryLoading, setSummaryLoading] = useState(false)

  const openSummary = async (periodId: string) => {
    setSummaryPeriodId(periodId)
    setSummary(null)
    setSummaryLoading(true)
    try {
      const data = await apiClient.get<DeductiblePeriodSummary>(
        `/billing/deductibles/periods/${periodId}/summary`,
      )
      setSummary(data)
    } catch (err) {
      showBanner(
        'error',
        `${t('progress.error')}: ${err instanceof ApiError ? err.message : String(err)}`,
      )
      setSummaryPeriodId(null)
    } finally {
      setSummaryLoading(false)
    }
  }

  const handleDownloadExcel = async (periodId: string) => {
    setExcelLoading(periodId)
    try {
      const token = await apiClient.getToken()
      const baseUrl = apiClient.baseUrl
      const resp = await fetch(
        `${baseUrl}/billing/deductibles/periods/${periodId}/excel`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!resp.ok) throw new Error('Excel download failed')
      const blob = await resp.blob()
      const disposition = resp.headers.get('Content-Disposition') || ''
      const match = disposition.match(/filename="?([^"]+)"?/)
      const filename = match?.[1] || `omavastuu_${periodId}.xlsx`
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      showBanner('error', (err as Error).message)
    } finally {
      setExcelLoading(null)
    }
  }

  const handleFetchIndex = () => {
    fetchIndexMutation.mutate({
      period_start: dateFrom,
      period_end: dateTo,
    })
  }

  const handleFetchSalesData = (exportUuid: string | null) => {
    fetchSalesDataMutation.mutate(batchBody(exportUuid))
  }

  const handleGenerateEntries = async (exportUuid: string | null) => {
    try {
      const preview = await previewMutation.mutateAsync(batchBody(exportUuid))
      setRegenConfirm({ exportUuid, preview })
    } catch (err) {
      showBanner('error', `${t('progress.error')}: ${(err as Error).message}`)
    }
  }

  const handleConfirmRegenerate = () => {
    if (!regenConfirm) return
    const exportUuid = regenConfirm.exportUuid
    setRegenConfirm(null)
    setProcessingRowKey(exportUuid ?? '_open')
    setProcessingPhase('entries')
    generateEntriesMutation.mutate(batchBody(exportUuid))
  }

  const toggleRow = (rowKey: string) => {
    setExpandedRow((prev) => (prev === rowKey ? null : rowKey))
  }

  const groups = data?.groups || []
  const totals = data?.totals
  const activeOp = data?.active_operation
  const isLocked = !!activeOp

  // Progress banner text based on phase
  const progressText = processingPhase === 'entries'
    ? t('progress.generatingEntries')
    : processingPhase === 'invoices'
      ? t('progress.creatingInvoices')
      : ''

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('title')}</h1>
        <p className="mt-1 text-gray-600">{t('subtitle')}</p>
      </div>

      {/* Banners */}
      {banner && (
        <div
          className={`rounded-lg px-4 py-3 flex items-center gap-2 ${
            banner.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          {banner.type === 'success' ? (
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
          )}
          {banner.message}
        </div>
      )}

      {fetchIndexMutation.isPending && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-blue-800 flex items-center gap-2">
          <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
          {t('progress.fetchingIndex')}
        </div>
      )}

      {processingRowKey && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 text-orange-800 flex items-center gap-2">
          <Loader2 className="w-4 h-4 text-orange-600 animate-spin" />
          {progressText}
        </div>
      )}

      {activeOp && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-lg px-4 py-3 text-yellow-800 flex items-center gap-2">
          <Loader2 className="w-4 h-4 text-yellow-600 animate-spin" />
          {t(`operations.${activeOp.operation_type}`, { defaultValue: activeOp.operation_type })}
          {' — '}
          {activeOp.started_by_name}
          {activeOp.started_at && ` (${formatDateTime(activeOp.started_at)})`}
        </div>
      )}

      {/* Date Range Filter */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('bills.dateFrom')}
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('bills.dateTo')}
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <button
            onClick={handleFetchIndex}
            disabled={!dateFrom || !dateTo || fetchIndexMutation.isPending || isLocked}
            className="inline-flex items-center px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('bills.fetchIndex')}
          </button>
        </div>
      </div>

      {/* Bills Summary Table */}
      {isLoading ? (
        <LoadingSpinner />
      ) : error ? (
        <div className="text-center py-12">
          <p className="text-red-600">{tc('errors.generic')}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="w-8 px-2 py-3" />
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('bills.exportBatch')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('bills.maxDate')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('bills.total')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('bills.notFetched')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('bills.fetched')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('bills.updateAvailable')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('bills.status')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {groups.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                      {t('bills.noBills')}
                    </td>
                  </tr>
                ) : (
                  <>
                    {groups.map((group) => {
                      const rowKey = group.bill_export_uuid || '_open'
                      const needsFetch =
                        group.not_fetched > 0 || group.update_available > 0
                      const allFetched = !needsFetch && group.total > 0
                      const isExpanded = expandedRow === rowKey
                      const canExpand = group.has_materials
                      const isProcessing = processingRowKey === rowKey
                      const matStatus = group.materials?.status

                      return (
                        <React.Fragment key={rowKey}>
                          <tr
                            className={`hover:bg-gray-50 ${
                              !group.bill_export_uuid ? 'bg-yellow-50' : ''
                            } ${canExpand ? 'cursor-pointer' : ''} ${
                              isExpanded ? 'bg-blue-50/30' : ''
                            }`}
                            onClick={() => canExpand && toggleRow(rowKey)}
                          >
                            {/* Expand icon */}
                            <td className="w-8 px-2 py-3 text-center">
                              {canExpand ? (
                                isExpanded ? (
                                  <ChevronDown className="w-4 h-4 text-gray-500 inline" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-gray-500 inline" />
                                )
                              ) : null}
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap font-medium text-gray-900">
                              {group.bill_export_uuid ? (
                                <span
                                  className="font-mono text-sm"
                                  title={group.bill_export_uuid}
                                >
                                  {formatUuid(group.bill_export_uuid)}
                                </span>
                              ) : (
                                <span className="text-yellow-700 font-medium">
                                  {t('bills.open')}
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                              {group.max_date || '\u2014'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right font-medium text-gray-900">
                              {group.total}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right text-gray-600">
                              <span
                                className={
                                  group.not_fetched > 0
                                    ? 'text-orange-600 font-medium'
                                    : ''
                                }
                              >
                                {group.not_fetched}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right text-gray-600">
                              <span
                                className={
                                  group.fetched > 0 ? 'text-green-600' : ''
                                }
                              >
                                {group.fetched}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right text-gray-600">
                              <span
                                className={
                                  group.update_available > 0
                                    ? 'text-blue-600 font-medium'
                                    : ''
                                }
                              >
                                {group.update_available}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right">
                              {needsFetch ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleFetchSalesData(group.bill_export_uuid)
                                  }}
                                  disabled={fetchSalesDataMutation.isPending || isLocked}
                                  className="inline-flex items-center px-3 py-1 text-sm bg-blue-50 text-blue-700 rounded hover:bg-blue-100 disabled:opacity-50"
                                >
                                  <Play className="w-3 h-3 mr-1" />
                                  {t('bills.fetchSalesData')}
                                </button>
                              ) : allFetched && !group.has_materials ? (
                                /* Phase 1: Generate entries */
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleGenerateEntries(group.bill_export_uuid)
                                  }}
                                  disabled={!!processingRowKey || isLocked}
                                  className="inline-flex items-center px-3 py-1 text-sm bg-orange-50 text-orange-700 rounded hover:bg-orange-100 disabled:opacity-50"
                                >
                                  {isProcessing ? (
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  ) : (
                                    <Settings className="w-3 h-3 mr-1" />
                                  )}
                                  {t('bills.generateEntries')}
                                </button>
                              ) : null}
                            </td>
                          </tr>

                          {/* Expanded row details */}
                          {isExpanded && group.materials && (
                            <tr key={rowKey + '-expanded'} className="bg-blue-50/20">
                              <td colSpan={8} className="px-6 py-4">
                                <div className="ml-8 bg-white rounded-lg border p-4 space-y-3">
                                  {/* Metadata */}
                                  <div className="text-sm text-gray-600">
                                    {t('bills.materialsGeneratedAt')}{' '}
                                    <span className="font-medium text-gray-900">
                                      {formatDateTime(group.materials.generated_at)}
                                    </span>
                                    {group.materials.generated_by_name && (
                                      <>
                                        {' '}({group.materials.generated_by_name})
                                      </>
                                    )}
                                  </div>

                                  {/* Action buttons */}
                                  <div className="flex flex-wrap gap-3">
                                    {/* Materials link — always visible */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        const exportParam = group.bill_export_uuid || '_open'
                                        navigate(
                                          `/payment-traffic/materials/${exportParam}`
                                        )
                                      }}
                                      className="inline-flex items-center px-4 py-2 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 text-sm"
                                    >
                                      <BarChart3 className="w-4 h-4 mr-2" />
                                      {t('bills.viewMaterials')} ({group.fetched})
                                    </button>

                                    {/* Invoices link — only for closed batches (not open bills) */}
                                    {group.bill_export_uuid && (matStatus === 'invoiced' || matStatus === 'sent' || matStatus === 'published') && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          const exportParam = group.bill_export_uuid || '_open'
                                          navigate(
                                            `/payment-traffic/invoices/${exportParam}`
                                          )
                                        }}
                                        className="inline-flex items-center px-4 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 text-sm"
                                      >
                                        <FileText className="w-4 h-4 mr-2" />
                                        {t('bills.viewInvoices')} ({group.materials.invoice_count})
                                      </button>
                                    )}

                                    {/* Regenerate entries — blocked when any invoice has been sent or published */}
                                    {matStatus !== 'sent' && matStatus !== 'published' && !group.materials?.any_sent && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleGenerateEntries(group.bill_export_uuid)
                                        }}
                                        disabled={!!processingRowKey || isLocked}
                                        className="inline-flex items-center px-4 py-2 bg-gray-50 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100 text-sm disabled:opacity-50"
                                      >
                                        {isProcessing ? (
                                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                          <RotateCcw className="w-4 h-4 mr-2" />
                                        )}
                                        {t('bills.regenerateEntries')}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      )
                    })}

                    {/* Totals row */}
                    {totals && groups.length > 0 && (
                      <tr className="bg-gray-50 font-medium border-t-2">
                        <td className="w-8 px-2 py-3" />
                        <td className="px-6 py-3 text-gray-700">
                          {t('bills.totalRow')}
                        </td>
                        <td className="px-6 py-3" />
                        <td className="px-4 py-3 text-right text-gray-900">
                          {totals.total}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600">
                          {totals.not_fetched}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600">
                          {totals.fetched}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600">
                          {totals.update_available}
                        </td>
                        <td className="px-4 py-3" />
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invoice Periods Section */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">{t('periods.title')}</h2>
          <button
            onClick={() => {
              setShowCreatePeriod(true)
              setNewPeriodOrg('')
              setNewPeriodStart('')
              setNewPeriodEnd('')
              setNewPeriodInvoiceDate('')
            }}
            className="inline-flex items-center px-3 py-1.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 text-sm"
          >
            <Plus className="w-4 h-4 mr-1" />
            {t('periods.newPeriod')}
          </button>
        </div>

        {periodsLoading ? (
          <div className="p-8"><LoadingSpinner /></div>
        ) : !periodsData?.data?.length ? (
          <div className="px-6 py-12 text-center text-gray-500">{t('periods.noPeriods')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('periods.organization')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('periods.period')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('periods.invoiceDate')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('periods.dueDate')}</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t('periods.status')}</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('periods.invoiceCount')}</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('periods.totalAmount')}</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('periods.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {periodsData.data.map((period) => {
                  const statusBadge: Record<string, string> = {
                    open: 'bg-gray-100 text-gray-700',
                    invoiced: 'bg-yellow-100 text-yellow-800',
                    sent: 'bg-blue-100 text-blue-800',
                    published: 'bg-green-100 text-green-800',
                  }
                  const statusLabel: Record<string, string> = {
                    open: t('periods.statusOpen'),
                    invoiced: t('periods.statusInvoiced'),
                    sent: t('periods.statusSent'),
                    published: t('periods.statusPublished'),
                  }
                  return (
                    <tr key={period.id} className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm text-gray-900">{period.organization_name || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {new Date(period.start_date).toLocaleDateString('fi-FI')} – {new Date(period.end_date).toLocaleDateString('fi-FI')}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {editingPeriod?.id === period.id ? (
                          <input
                            type="date"
                            value={editingPeriod.invoice_date}
                            onChange={(e) => setEditingPeriod({ ...editingPeriod, invoice_date: e.target.value })}
                            className="border border-gray-300 rounded px-2 py-1 text-sm w-36"
                          />
                        ) : (
                          new Date(period.invoice_date).toLocaleDateString('fi-FI')
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {editingPeriod?.id === period.id ? (
                          <input
                            type="date"
                            value={editingPeriod.due_date}
                            onChange={(e) => setEditingPeriod({ ...editingPeriod, due_date: e.target.value })}
                            className="border border-gray-300 rounded px-2 py-1 text-sm w-36"
                          />
                        ) : (
                          period.due_date ? new Date(period.due_date).toLocaleDateString('fi-FI') : '—'
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge[period.status] || 'bg-gray-100 text-gray-700'}`}>
                          {statusLabel[period.status] || period.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-gray-600">{period.invoice_count}</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-600">
                        {period.total_amount != null
                          ? Number(period.total_amount).toLocaleString('fi-FI', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {editingPeriod?.id === period.id ? (
                          <div className="flex gap-1 justify-end">
                            <button
                              onClick={() => {
                                const body: { invoice_date?: string; due_date?: string } = {}
                                if (editingPeriod.invoice_date !== period.invoice_date) body.invoice_date = editingPeriod.invoice_date
                                if (editingPeriod.due_date !== (period.due_date || '')) body.due_date = editingPeriod.due_date || undefined
                                if (Object.keys(body).length > 0) {
                                  updatePeriodMutation.mutate({ periodId: period.id, body })
                                } else {
                                  setEditingPeriod(null)
                                }
                              }}
                              disabled={updatePeriodMutation.isPending}
                              className="inline-flex items-center px-2 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100 disabled:opacity-50"
                            >
                              <Check className="w-3 h-3 mr-1" />
                              {tc('save')}
                            </button>
                            <button
                              onClick={() => setEditingPeriod(null)}
                              className="inline-flex items-center px-2 py-1 text-xs bg-gray-50 text-gray-600 rounded hover:bg-gray-100"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <>
                        {period.status === 'open' && (
                          <div className="flex gap-1 justify-end">
                            <button
                              onClick={() => setEditingPeriod({ id: period.id, invoice_date: period.invoice_date, due_date: period.due_date || '' })}
                              className="inline-flex items-center px-2 py-1 text-xs bg-gray-50 text-gray-600 rounded hover:bg-gray-100"
                              title={t('periods.editDates')}
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => createPeriodInvoicesMutation.mutate(period.id)}
                              disabled={createPeriodInvoicesMutation.isPending || isLocked}
                              className="inline-flex items-center px-2 py-1 text-xs bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100 disabled:opacity-50"
                              title={t('periods.createInvoices')}
                            >
                              <FileText className="w-3 h-3 mr-1" />
                              {t('periods.createInvoices')}
                            </button>
                            <button
                              onClick={() => {
                                if (confirm(t('periods.confirmDelete'))) deletePeriodMutation.mutate(period.id)
                              }}
                              className="inline-flex items-center px-2 py-1 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100"
                              title={t('periods.deletePeriod')}
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                        {period.status === 'invoiced' && (
                          <div className="flex gap-1 justify-end">
                            <button
                              onClick={() => setEditingPeriod({ id: period.id, invoice_date: period.invoice_date, due_date: period.due_date || '' })}
                              className="inline-flex items-center px-2 py-1 text-xs bg-gray-50 text-gray-600 rounded hover:bg-gray-100"
                              title={t('periods.editDates')}
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => navigate(`/payment-traffic/invoices/_period_${period.id}?invoice_period_id=${period.id}`)}
                              className="inline-flex items-center px-2 py-1 text-xs bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100"
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              {t('periods.viewInvoices')}
                            </button>
                            <button
                              onClick={() => {
                                if (confirm(t('periods.confirmDeleteInvoices'))) deletePeriodInvoicesMutation.mutate(period.id)
                              }}
                              className="inline-flex items-center px-2 py-1 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                        {period.status === 'sent' && (
                          <button
                            onClick={() => navigate(`/payment-traffic/invoices/_period_${period.id}?invoice_period_id=${period.id}`)}
                            className="inline-flex items-center px-2 py-1 text-xs bg-orange-50 text-orange-700 rounded hover:bg-orange-100"
                          >
                            <Send className="w-3 h-3 mr-1" />
                            {t('periods.viewInvoices')}
                          </button>
                        )}
                        {period.status === 'published' && (
                          <button
                            onClick={() => navigate(`/payment-traffic/invoices/_period_${period.id}?invoice_period_id=${period.id}`)}
                            className="inline-flex items-center px-2 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100"
                          >
                            <Lock className="w-3 h-3 mr-1" />
                            {t('periods.viewInvoices')}
                          </button>
                        )}
                          </>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Deductible Periods Section */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">{t('deductibles.title')}</h2>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/payment-traffic/deductible-invoices')}
              className="inline-flex items-center px-3 py-1.5 bg-white border text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
            >
              <FileText className="w-4 h-4 mr-1" />
              {t('deductibleInvoices.title')}
            </button>
            <button
              onClick={() => {
                setShowCreateDeductible(true)
                setNewDeductibleOrg('')
                setNewDeductibleMonth('')
              }}
              className="inline-flex items-center px-3 py-1.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 text-sm"
            >
              <Plus className="w-4 h-4 mr-1" />
              {t('deductibles.newPeriod')}
            </button>
          </div>
        </div>

        {deductiblePeriodsLoading ? (
          <div className="p-8"><LoadingSpinner /></div>
        ) : !deductiblePeriodsData?.length ? (
          <div className="px-6 py-12 text-center text-gray-500">{t('deductibles.noPeriods')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('deductibles.organization')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('deductibles.period')}</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t('deductibles.status')}</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('deductibles.lineCount')}</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('deductibles.totalAmount')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('deductibles.invoiceNumbers')}</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('deductibles.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {deductiblePeriodsData.map((period) => {
                  const statusBadge: Record<string, string> = {
                    open: 'bg-gray-100 text-gray-700',
                    processing: 'bg-blue-100 text-blue-800',
                    cancelling: 'bg-orange-100 text-orange-800',
                    generated: 'bg-yellow-100 text-yellow-800',
                    approved: 'bg-green-100 text-green-800',
                  }
                  const statusLabel: Record<string, string> = {
                    open: t('deductibles.statusOpen'),
                    processing: t('deductibles.statusProcessing'),
                    cancelling: t('deductibles.statusCancelling'),
                    generated: t('deductibles.statusGenerated'),
                    approved: t('deductibles.statusApproved'),
                  }
                  const phaseLabel: Record<string, string> = {
                    bills: t('deductibles.phaseBills'),
                    customers: t('deductibles.phaseCustomers'),
                    lines: t('deductibles.phaseLines'),
                    corrections: t('deductibles.phaseCorrections'),
                  }
                  // Normal period = 1st of month → "YYYY-MM"; a corrections-only
                  // period uses another day of the month → show the full date.
                  const periodLabel = period.year_month.endsWith('-01')
                    ? period.year_month.slice(0, 7)
                    : period.year_month
                  const copayment = Number(period.total_copayment)

                  // Build processing status text: phase label + progress counter
                  const processingText = (() => {
                    if (period.status === 'cancelling') return statusLabel.cancelling
                    const phase = period.progress_phase
                    const label = phase ? phaseLabel[phase] || phase : statusLabel.processing
                    if ((phase === 'customers' || phase === 'corrections') && period.progress_total) {
                      return `${label}: ${period.progress_current || 0} / ${period.progress_total}`
                    }
                    return label
                  })()

                  return (
                    <tr key={period.id} className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm text-gray-900">{period.organization_name || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{periodLabel}</td>
                      <td className="px-4 py-3 text-center">
                        {(period.status === 'processing' || period.status === 'cancelling') ? (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge[period.status]}`}>
                            <Loader2 className="w-3 h-3 animate-spin" />
                            {processingText}
                          </span>
                        ) : (
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge[period.status] || 'bg-gray-100 text-gray-700'}`}>
                            {statusLabel[period.status] || period.status}
                          </span>
                        )}
                        {period.status === 'open' && period.error_message && (
                          <div
                            className="mt-1 flex items-start gap-1 text-left text-xs text-red-700 max-w-xs"
                            title={period.error_message}
                          >
                            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                            <span>
                              <span className="font-medium">{t('deductibles.generationFailed')}</span>{' '}
                              {period.error_message}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-gray-600">{period.line_count}</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-600">
                        {copayment > 0
                          ? copayment.toLocaleString('fi-FI', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-left text-sm text-gray-600 whitespace-nowrap">
                        {period.invoice_number_first && period.invoice_number_last
                          ? period.invoice_number_first === period.invoice_number_last
                            ? String(period.invoice_number_first)
                            : `${period.invoice_number_first} – ${period.invoice_number_last}`
                          : '—'}
                        {period.planned_credit_note_count != null &&
                          period.planned_credit_note_count > 0 && (
                            <span
                              className="ml-2 inline-block px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700"
                              title={t('deductibles.creditNoteBadgeTitle', {
                                count: period.planned_credit_note_count,
                                defaultValue: `${period.planned_credit_note_count} asiakkaan lasku jää negatiiviseksi → erillinen hyvityslasku`,
                              })}
                            >
                              {t('deductibles.creditNoteBadge', {
                                count: period.planned_credit_note_count,
                                defaultValue: `${period.planned_credit_note_count} hyvityslaskua`,
                              })}
                            </span>
                          )}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {period.status === 'open' && (
                          <div className="flex gap-1 justify-end">
                            <button
                              onClick={() => generateDeductibleMutation.mutate(period.id)}
                              disabled={generateDeductibleMutation.isPending}
                              className="inline-flex items-center px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100 disabled:opacity-50"
                              title={t('deductibles.generate')}
                            >
                              <Settings className="w-3 h-3 mr-1" />
                              {t('deductibles.generate')}
                            </button>
                            <button
                              onClick={() => {
                                if (confirm(t('deductibles.confirmDelete'))) deleteDeductibleMutation.mutate(period.id)
                              }}
                              className="inline-flex items-center px-2 py-1 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100"
                              title={t('deductibles.deletePeriod')}
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                        {period.status === 'processing' && (
                          <div className="flex gap-1 items-center justify-end">
                            <button
                              onClick={() => cancelDeductibleMutation.mutate(period.id)}
                              disabled={cancelDeductibleMutation.isPending}
                              className="inline-flex items-center px-2 py-1 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100 disabled:opacity-50"
                              title={t('deductibles.cancelGeneration')}
                            >
                              <X className="w-3 h-3 mr-1" />
                              {t('deductibles.cancelGeneration')}
                            </button>
                          </div>
                        )}
                        {period.status === 'cancelling' && (
                          <span className="text-xs text-orange-600">
                            <Loader2 className="w-3 h-3 animate-spin inline mr-1" />
                            {t('deductibles.statusCancelling')}
                          </span>
                        )}
                        {period.status === 'generated' && (
                          <div className="flex gap-1 justify-end">
                            <button
                              onClick={() => openSummary(period.id)}
                              className="inline-flex items-center px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                              title={t('deductibles.summaryTitle')}
                            >
                              <Info className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => handleDownloadExcel(period.id)}
                              disabled={excelLoading === period.id}
                              className="inline-flex items-center px-2 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100 disabled:opacity-50"
                              title={t('deductibles.downloadExcel')}
                            >
                              {excelLoading === period.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                              Excel
                            </button>
                            <button
                              onClick={() => handleDownloadDat(period.id, 'las')}
                              className="inline-flex items-center px-2 py-1 text-xs bg-gray-50 text-gray-600 rounded hover:bg-gray-100"
                              title={t('deductibles.downloadPreviewLas')}
                            >
                              <Download className="w-3 h-3" />
                              LAS
                            </button>
                            <button
                              onClick={() => handleDownloadDat(period.id, 'lar')}
                              className="inline-flex items-center px-2 py-1 text-xs bg-gray-50 text-gray-600 rounded hover:bg-gray-100"
                              title={t('deductibles.downloadPreviewLar')}
                            >
                              <Download className="w-3 h-3" />
                              LAR
                            </button>
                            <button
                              onClick={() => generateDeductibleMutation.mutate(period.id)}
                              disabled={generateDeductibleMutation.isPending}
                              className="inline-flex items-center px-2 py-1 text-xs bg-orange-50 text-orange-700 rounded hover:bg-orange-100 disabled:opacity-50"
                              title={t('deductibles.regenerate')}
                            >
                              <RotateCcw className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => {
                                if (confirm(t('deductibles.confirmApprove'))) approveDeductibleMutation.mutate(period.id)
                              }}
                              disabled={approveDeductibleMutation.isPending}
                              className="inline-flex items-center px-2 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100 disabled:opacity-50"
                              title={t('deductibles.approve')}
                            >
                              <ShieldCheck className="w-3 h-3 mr-1" />
                              {t('deductibles.approve')}
                            </button>
                            <button
                              onClick={() => {
                                if (confirm(t('deductibles.confirmDelete'))) deleteDeductibleMutation.mutate(period.id)
                              }}
                              className="inline-flex items-center px-2 py-1 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100"
                              title={t('deductibles.deletePeriod')}
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                        {period.status === 'approved' && (
                          <div className="flex gap-1 justify-end">
                            <button
                              onClick={() => openSummary(period.id)}
                              className="inline-flex items-center px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                              title={t('deductibles.summaryTitle')}
                            >
                              <Info className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => handleDownloadExcel(period.id)}
                              disabled={excelLoading === period.id}
                              className="inline-flex items-center px-2 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100 disabled:opacity-50"
                              title={t('deductibles.downloadExcel')}
                            >
                              {excelLoading === period.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                              Excel
                            </button>
                            <button
                              onClick={() => handleDownloadDat(period.id, 'las')}
                              className="inline-flex items-center px-2 py-1 text-xs bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100"
                              title={t('deductibles.downloadLas')}
                            >
                              <Download className="w-3 h-3 mr-1" />
                              LAS
                            </button>
                            <button
                              onClick={() => handleDownloadDat(period.id, 'lar')}
                              className="inline-flex items-center px-2 py-1 text-xs bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100"
                              title={t('deductibles.downloadLar')}
                            >
                              <Download className="w-3 h-3 mr-1" />
                              LAR
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Deductible material summary dialog */}
      {summaryPeriodId && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setSummaryPeriodId(null)}
        >
          <div
            className="bg-white rounded-lg max-w-lg w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-600" />
                {t('deductibles.summaryTitle')}
              </h3>
              <button onClick={() => setSummaryPeriodId(null)}>
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {summaryLoading || !summary ? (
              <div className="flex items-center justify-center gap-2 text-gray-500 py-8">
                <Loader2 className="w-5 h-5 animate-spin" />
                {t('deductibles.summaryLoading')}
              </div>
            ) : (
              <div className="space-y-4 text-sm">
                <p className="text-gray-600">
                  {summary.organization_name} —{' '}
                  {summary.year_month.endsWith('-01') ? summary.year_month.slice(0, 7) : summary.year_month}
                </p>

                <div>
                  <h4 className="font-semibold text-gray-800 border-b pb-1 mb-1">
                    {t('deductibles.summaryRows')}
                  </h4>
                  <div className="flex justify-between py-0.5">
                    <span className="text-gray-600">{t('deductibles.summaryLas')}</span>
                    <span className="font-mono">{summary.las_line_count.toLocaleString('fi-FI')}</span>
                  </div>
                  <div className="flex justify-between py-0.5">
                    <span className="text-gray-600">{t('deductibles.summaryLar')}</span>
                    <span className="font-mono">{summary.lar_line_count.toLocaleString('fi-FI')}</span>
                  </div>
                  <div className="flex justify-between py-0.5 text-gray-400">
                    <span>{t('deductibles.summaryZeroed')}</span>
                    <span className="font-mono">{summary.zeroed_line_count.toLocaleString('fi-FI')}</span>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 border-b pb-1 mb-1">
                    {t('deductibles.summaryInvoices')}
                  </h4>
                  <div className="flex justify-between py-0.5">
                    <span className="text-gray-600">{t('deductibles.summaryInvoiceTotal')}</span>
                    <span className="font-mono">{summary.invoice_count_total.toLocaleString('fi-FI')}</span>
                  </div>
                  <div className="flex justify-between py-0.5">
                    <span className="text-gray-600">{t('deductibles.summaryChargeInvoices')}</span>
                    <span className="font-mono">{summary.charge_invoice_count.toLocaleString('fi-FI')}</span>
                  </div>
                  <div className="flex justify-between py-0.5">
                    <span className="text-gray-600">{t('deductibles.summaryCreditInvoices')}</span>
                    <span className="font-mono text-blue-700">{summary.credit_invoice_count.toLocaleString('fi-FI')}</span>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 border-b pb-1 mb-1">
                    {t('deductibles.summaryAmounts')}
                  </h4>
                  <div className="flex justify-between py-0.5">
                    <span className="text-gray-600">{t('deductibles.summaryTotalAmount')}</span>
                    <span className="font-mono font-semibold">{formatEur(summary.total_amount)}</span>
                  </div>
                  <div className="flex justify-between py-0.5">
                    <span className="text-gray-600">{t('deductibles.summaryChargeAmount')}</span>
                    <span className="font-mono">{formatEur(summary.charge_amount)}</span>
                  </div>
                  <div className="flex justify-between py-0.5">
                    <span className="text-gray-600">{t('deductibles.summaryCreditAmount')}</span>
                    <span className="font-mono text-blue-700">{formatEur(summary.credit_amount)}</span>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 border-b pb-1 mb-1">
                    {t('deductibles.summaryCorrections')}
                  </h4>
                  <div className="flex justify-between py-0.5">
                    <span className="text-gray-600">{t('deductibles.summaryOriginalLines')}</span>
                    <span className="font-mono">{summary.original_line_count.toLocaleString('fi-FI')}</span>
                  </div>
                  <div className="flex justify-between py-0.5">
                    <span className="text-gray-600">{t('deductibles.summaryCreditNoteLines')}</span>
                    <span className="font-mono">{summary.credit_note_line_count.toLocaleString('fi-FI')}</span>
                  </div>
                  <div className="flex justify-between py-0.5">
                    <span className="text-gray-600">{t('deductibles.summaryMixedCreditLines')}</span>
                    <span className="font-mono">{summary.mixed_credit_line_count.toLocaleString('fi-FI')}</span>
                  </div>
                  <div className="flex justify-between py-0.5">
                    <span className="text-gray-600">{t('deductibles.summaryCorrectionLines')}</span>
                    <span className="font-mono">{summary.correction_line_count.toLocaleString('fi-FI')}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Deductible Period Dialog */}
      {showCreateDeductible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">{t('deductibles.newPeriod')}</h3>
              <button onClick={() => setShowCreateDeductible(false)}>
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('deductibles.organization')}</label>
                <select
                  value={newDeductibleOrg}
                  onChange={(e) => setNewDeductibleOrg(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">{t('deductibles.selectOrg')}</option>
                  {orgsWithFina.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('deductibles.selectMonth')}</label>
                <input
                  type="month"
                  value={newDeductibleMonth}
                  onChange={(e) => setNewDeductibleMonth(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            {createDeductibleMutation.isError && (
              <div className="mt-3 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
                {(createDeductibleMutation.error as Error).message}
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCreateDeductible(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                {t('deductibles.cancel')}
              </button>
              <button
                onClick={() => {
                  createDeductibleMutation.mutate({
                    organization_id: newDeductibleOrg,
                    year_month: newDeductibleMonth + '-01',
                  })
                }}
                disabled={!newDeductibleOrg || !newDeductibleMonth || createDeductibleMutation.isPending}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createDeductibleMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  t('deductibles.createPeriod')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Regeneration Confirmation Dialog */}
      {regenConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">{t('bills.regenerateConfirmTitle')}</h3>
              <button onClick={() => setRegenConfirm(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3 mb-6">
              <p className="text-sm text-gray-600">{t('bills.regenerateConfirmDescription')}</p>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{t('bills.totalBills')}</span>
                  <span className="font-medium">{regenConfirm.preview.total_bills}</span>
                </div>
                <div className="flex justify-between text-sm text-green-700">
                  <span>{t('bills.freeBills')}</span>
                  <span className="font-medium">{regenConfirm.preview.free_bills}</span>
                </div>
                <div className="flex justify-between text-sm text-amber-700">
                  <span>{t('bills.invoicedBills')}</span>
                  <span className="font-medium">{regenConfirm.preview.invoiced_bills}</span>
                </div>
              </div>
              <p className="text-xs text-amber-600">{t('bills.regenerateInvoicedWarning')}</p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setRegenConfirm(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
              >
                {tc('cancel')}
              </button>
              <button
                onClick={handleConfirmRegenerate}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
              >
                {t('bills.regenerateConfirm', { count: regenConfirm.preview.free_bills })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Period Dialog */}
      {showCreatePeriod && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">{t('periods.newPeriod')}</h3>
              <button onClick={() => setShowCreatePeriod(false)}>
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('periods.organization')}</label>
                <select
                  value={newPeriodOrg}
                  onChange={(e) => setNewPeriodOrg(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">{t('periods.selectOrg')}</option>
                  {orgsWithAhola.map((org) => (
                    <option key={org.id} value={org.ahola_sub_org_uuid || ''}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('periods.startDate')}</label>
                  <input
                    type="date"
                    value={newPeriodStart}
                    onChange={(e) => setNewPeriodStart(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('periods.endDate')}</label>
                  <input
                    type="date"
                    value={newPeriodEnd}
                    onChange={(e) => setNewPeriodEnd(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('periods.invoiceDate')}</label>
                  <input
                    type="date"
                    value={newPeriodInvoiceDate}
                    onChange={(e) => setNewPeriodInvoiceDate(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('periods.dueDate')}</label>
                  <input
                    type="date"
                    value={newPeriodDueDate}
                    onChange={(e) => setNewPeriodDueDate(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>

            {createPeriodMutation.isError && (
              <div className="mt-3 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
                {(createPeriodMutation.error as Error).message}
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCreatePeriod(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                {t('periods.cancel')}
              </button>
              <button
                onClick={() => {
                  createPeriodMutation.mutate({
                    sub_organization_uuid: newPeriodOrg,
                    start_date: newPeriodStart,
                    end_date: newPeriodEnd,
                    invoice_date: newPeriodInvoiceDate,
                    ...(newPeriodDueDate ? { due_date: newPeriodDueDate } : {}),
                  })
                }}
                disabled={!newPeriodOrg || !newPeriodStart || !newPeriodEnd || !newPeriodInvoiceDate || createPeriodMutation.isPending}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createPeriodMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  t('periods.createPeriod')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
